import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Quality } from "../api/types";

/* ---------------------------------------------------------------- icônes (traits 1,6 px, 16×16) */
const P: Record<string, string> = {
  search: "M7 12.5A5.5 5.5 0 1 0 7 1.5a5.5 5.5 0 0 0 0 11ZM11 11l3.5 3.5",
  upload: "M8 10.5V2M4.5 5.5 8 2l3.5 3.5M2.5 10.5v2A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-2",
  download: "M8 2v8.5M4.5 7 8 10.5 11.5 7M2.5 10.5v2A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-2",
  x: "M4 4l8 8M12 4l-8 8",
  plus: "M8 3v10M3 8h10",
  filter: "M2 3.5h12L9.5 9v4l-3 1.5V9z",
  theme: "M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2Zm0 0v12",
  logout: "M6 14H3.5A1.5 1.5 0 0 1 2 12.5v-9A1.5 1.5 0 0 1 3.5 2H6M10.5 11.5 14 8l-3.5-3.5M14 8H6",
  edit: "M10.5 2.5l3 3L6 13H3v-3z",
  merge: "M4 2v4a4 4 0 0 0 4 4h4M9 7l3 3-3 3M4 14v-2",
  check: "M3 8.5 6.5 12 13 4.5",
  copy: "M5.5 5.5h8v8h-8zM10.5 5.5v-3h-8v8h3",
  phone: "M4 2h2.5l1 3-1.5 1a8 8 0 0 0 4 4l1-1.5 3 1V12a2 2 0 0 1-2 2A10 10 0 0 1 2 4a2 2 0 0 1 2-2Z",
  mail: "M2 4h12v8H2zM2 4l6 5 6-5",
  globe: "M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2ZM2 8h12M8 2c2 2 2 10 0 12M8 2c-2 2-2 10 0 12",
  users: "M6 7.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.5 14a4.5 4.5 0 0 1 9 0M11 2.7a2.5 2.5 0 0 1 0 4.6M12.5 9.6A4.5 4.5 0 0 1 14.5 14",
  history: "M2.5 8a5.5 5.5 0 1 0 1.6-3.9M2.5 2.5v2.6h2.6M8 5v3l2 1.5",
  alert: "M8 2 1.5 13.5h13zM8 6.5v3.5M8 12h.01",
  key: "M10 9.5a3.5 3.5 0 1 0-3.4-2.7L2 11.4V14h2.6v-1.5h1.5V11h1.5l1-1a3.5 3.5 0 0 0 1.4.5ZM11 5h.01",
  power: "M8 2v5.5M4.6 4.2a5.5 5.5 0 1 0 6.8 0",
  trash: "M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5h6.6L12 4M6.8 6.5v5M9.2 6.5v5",
};

export function Icon({ name, size = 16, className }: { name: keyof typeof P | string; size?: number; className?: string }) {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={P[name] ?? ""} />
    </svg>
  );
}

export function Logo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 22 22" aria-hidden="true">
      <defs>
        <linearGradient id="lg" x1="0" x2="1">
          <stop offset="0" stopColor="var(--hero-a)" />
          <stop offset="1" stopColor="var(--hero-b)" />
        </linearGradient>
      </defs>
      <rect x="1" y="12" width="5" height="9" rx="2" fill="url(#lg)" />
      <rect x="8.5" y="6" width="5" height="15" rx="2" fill="url(#lg)" />
      <rect x="16" y="1" width="5" height="20" rx="2" fill="url(#lg)" />
    </svg>
  );
}

/* ---------------------------------------------------------------- statuts & couleurs */
export type Tone = "blue" | "green" | "red" | "orange" | "purple" | "gray";

export function Chip({ tone = "gray", solid, dot, children, title }: { tone?: Tone; solid?: boolean; dot?: boolean; children: ReactNode; title?: string }) {
  return <span className={`chip c-${tone}${solid ? " solid" : ""}${dot ? " dot" : ""}`} title={title}>{children}</span>;
}

export const QUALITY: Record<Quality, { label: string; tone: Tone }> = {
  valid: { label: "Valide", tone: "green" },
  doubtful: { label: "Douteux", tone: "orange" },
  invalid: { label: "Invalide", tone: "red" },
  verified: { label: "Vérifié", tone: "blue" },
  empty: { label: "Vide", tone: "gray" },
};

export function QualityDot({ q, reason }: { q: Quality | undefined; reason?: string | null }) {
  const k = q ?? "valid";
  return <span className={`qdot q-${k}`} role="img" aria-label={QUALITY[k].label} title={reason ? `${QUALITY[k].label} — ${reason}` : QUALITY[k].label} />;
}

export const IMPORT_STATUS: Record<string, { label: string; tone: Tone }> = {
  uploaded: { label: "Reçu", tone: "gray" },
  analyzing: { label: "Analyse…", tone: "blue" },
  awaiting_mapping: { label: "Mapping à valider", tone: "purple" },
  queued: { label: "En file", tone: "blue" },
  running: { label: "En cours", tone: "blue" },
  completed: { label: "Terminé", tone: "green" },
  failed: { label: "Échec", tone: "red" },
  cancelled: { label: "Annulé", tone: "gray" },
};

export const CHANGE: Record<string, { label: string; tone: Tone }> = {
  create: { label: "Création", tone: "green" },
  enrich: { label: "Enrichi", tone: "blue" },
  append: { label: "Ajout", tone: "blue" },
  correct: { label: "Corrigé", tone: "green" },
  update: { label: "Modifié", tone: "purple" },
  conflict: { label: "Conflit conservé", tone: "orange" },
  merge: { label: "Fusion", tone: "purple" },
  merged_into: { label: "Fusionné", tone: "gray" },
};

export const SOURCE: Record<string, string> = {
  import: "Import", manual: "Saisie", merge: "Fusion", prospection: "Prospection", system: "Système",
};

export const DATA_TYPES: Record<string, string> = {
  text: "Texte", integer: "Entier", decimal: "Décimal", date: "Date", boolean: "Oui / non", phone: "Téléphone",
  email: "Email", url: "URL", siret: "SIRET", siren: "SIREN", naf: "Code NAF", postal_code: "Code postal",
  city: "Ville", address: "Adresse", company_name: "Raison sociale", person_name: "Nom de personne",
  civility: "Civilité", function: "Fonction",
};

export const FUNCTIONS: Record<string, string> = {
  DIRIGEANT: "Dirigeant", FINANCE: "Finance", RH: "RH", ACHATS: "Achats", COMMERCIAL: "Commercial",
  MARKETING: "Marketing", IT: "Informatique", TECHNIQUE: "Technique", FLOTTE: "Flotte", ASSISTANAT: "Assistanat",
  AUTRE: "Autre",
};

/* ---------------------------------------------------------------- blocs */
export function Kpi({ label, value, sub, tone, focus }: { label: string; value: ReactNode; sub?: ReactNode; tone?: "good" | "warn" | "bad" | "info"; focus?: boolean }) {
  return (
    <div className={`tile kpi${tone ? " " + tone : ""}${focus ? " focus" : ""}`}>
      <span className="lbl">{label}</span>
      <b className="num">{value}</b>
      {sub != null && <span className="sub">{sub}</span>}
    </div>
  );
}

export function Spinner({ label = "Chargement" }: { label?: string }) {
  return <span className="spin" role="status" aria-label={label} />;
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <b>{title}</b>
      {children && <span>{children}</span>}
    </div>
  );
}

export function PageHead({ eyebrow, title, right }: { eyebrow?: ReactNode; title: ReactNode; right?: ReactNode }) {
  return (
    <div className="phead">
      <div>
        {eyebrow && <div className="ey">{eyebrow}</div>}
        <h1>{title}</h1>
      </div>
      {right && <div className="right">{right}</div>}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: [T, ReactNode][]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(([v, t]) => (
        <button key={v} type="button" aria-pressed={v === value} onClick={() => onChange(v)}>{t}</button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- tiroir (Échap, focus, retour du focus) */
export function Drawer({ open, onClose, title, subtitle, actions, children }: { open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : "Détail"} tabIndex={-1} ref={ref}>
        <header>
          <div className="stack" style={{ gap: 4, minWidth: 0 }}>
            <h2 style={{ overflowWrap: "anywhere" }}>{title}</h2>
            {subtitle}
          </div>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            {actions}
            <button className="btn icon" type="button" onClick={onClose} aria-label="Fermer"><Icon name="x" /></button>
          </div>
        </header>
        <div className="dbody">{children}</div>
      </aside>
    </div>
  );
}

/* ---------------------------------------------------------------- toasts */
type ToastT = { id: number; text: string; bad?: boolean };
const ToastCtx = createContext<(text: string, bad?: boolean) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastT[]>([]);
  const push = useCallback((text: string, bad?: boolean) => {
    const id = Date.now() + Math.random();
    setItems((l) => [...l.slice(-2), { id, text, bad }]);
    setTimeout(() => setItems((l) => l.filter((t) => t.id !== id)), bad ? 6500 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {items.map((t) => <div key={t.id} className={`toast${t.bad ? " bad" : ""}`}>{t.text}</div>)}
      </div>
    </ToastCtx.Provider>
  );
}
